/**
 * Universe Requests — Compliance-facing tool.
 *
 * Compliance-only. Lives in Core Tools. Lets a Compliance Officer:
 *   - pick one FDR (from the registry, or add inline)
 *   - pick one or more universes (Scrubber audit + Reporter reporting + future)
 *   - send a single secure request, see status, follow up
 *
 * Visual cadence mirrors Reporter / PPM: tabs, light cards, cardinal red
 * accents on numbers + section IDs, mono labels.
 */

import React, { useState, useEffect } from 'react';
import RequestsList from './RequestsList';
import NewRequestWizard from './NewRequestWizard';
import RequestDetail from './RequestDetail';
import FdrRegistry from './FdrRegistry';
import SchedulesList from './SchedulesList';
import ScheduleWizard from './ScheduleWizard';
import Correspondence from './Correspondence';

const API = process.env.REACT_APP_API_URL;

const TABS = [
  { id: 'requests',       label: 'Requests',       icon: '⇄' },
  { id: 'schedules',      label: 'Schedules',      icon: '◷' },
  { id: 'correspondence', label: 'Correspondence', icon: '✉' },
  { id: 'fdrs',           label: 'FDRs',           icon: '◉' },
];

export default function UniverseRequests() {
  const [view, setView] = useState('requests');
  const [selectedId, setSelectedId] = useState(null);

  return (
    // h-full + overflow-y-auto: the app shell mounts this page inside a
    // fixed-height overflow-hidden frame (App.js), so the page must own its
    // vertical scrolling. min-h-screen assumed a scrolling window that does
    // not exist there, which clipped anything below the fold (the wizard's
    // Next button) with no way to reach it.
    <div className="h-full overflow-y-auto bg-slate-50">
      <div className="border-b border-slate-200 bg-white">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-baseline gap-3">
            <div className="text-xs font-mono uppercase tracking-widest text-red-700">PCG · 15</div>
            <h1 className="text-xl font-semibold text-slate-900">Universe Requests</h1>
            <div className="text-xs text-slate-500 font-mono">FDR portal for audit + reporting universes</div>
          </div>
        </div>
        {view !== 'detail' && view !== 'new' && (
          <div className="max-w-7xl mx-auto px-6 flex gap-1">
            {TABS.map(t => (
              <button
                key={t.id}
                onClick={() => { setView(t.id); setSelectedId(null); }}
                className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                  view === t.id
                    ? 'border-red-700 text-red-700'
                    : 'border-transparent text-slate-600 hover:text-slate-900'
                }`}
              >
                <span className="mr-2 font-mono text-xs">{t.icon}</span>
                {t.label}
              </button>
            ))}
            <div className="ml-auto py-2 flex gap-2">
              {view === 'schedules' ? (
                <button
                  onClick={() => setView('new-schedule')}
                  className="bg-red-700 hover:bg-red-800 text-white px-4 py-1.5 text-sm font-semibold transition-colors"
                >
                  New Schedule
                </button>
              ) : (
                <button
                  onClick={() => setView('new')}
                  className="bg-red-700 hover:bg-red-800 text-white px-4 py-1.5 text-sm font-semibold transition-colors"
                >
                  New Request
                </button>
              )}
            </div>
          </div>
        )}
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {view === 'requests' && (
          <RequestsList onOpen={id => { setSelectedId(id); setView('detail'); }} />
        )}
        {view === 'schedules' && (
          <SchedulesList onNew={() => setView('new-schedule')} />
        )}
        {view === 'correspondence' && <Correspondence />}
        {view === 'fdrs' && <FdrRegistry />}
        {view === 'new' && (
          <NewRequestWizard
            onCancel={() => setView('requests')}
            onCreated={(req) => { setSelectedId(req.id); setView('detail'); }}
          />
        )}
        {view === 'new-schedule' && (
          <ScheduleWizard
            onCancel={() => setView('schedules')}
            onCreated={() => setView('schedules')}
          />
        )}
        {view === 'detail' && selectedId && (
          <RequestDetail
            requestId={selectedId}
            onBack={() => { setSelectedId(null); setView('requests'); }}
          />
        )}
      </div>
    </div>
  );
}
